import { Mutation } from 'vuex-module-decorators';
import { InjectModule, InjectVuexModule } from 'vue-di/vuex';
import moment, { Moment } from 'moment';
import { Gender, AlternateCardType } from '@trialcard/enums';
import { EdgePatientEnrollmentApi, ApigatewayApi, EdgeCouponApi, EdgeEnrollmentApi, EdgeSiteAccountApi, EdgeClaimApi , EdgeCardApi } from '@trialcard/apigateway.client';
import { Resolve } from 'vue-di';
import {
    EnrollmentDemographicsModel,
    EnrollmentClientIdentifiersModel,
    EnrollmentInsuranceVerificationModel,
    SendRequestModel,
    EmailSendEmailSingle,
    ClaimV1ClaimGroupViewSearchGroupViewSearchRequest,
    ClaimClaimGroupViewExtendedModel,
    BasicEnrollmentRequest,
    BasicEnrollmentPharmacyPayor,
} from '@trialcard/apigateway.models';
import _, { result } from 'lodash';
import { OnClear } from '~/services/clearModule';
import { clearRecords } from '~/services/clearRecords';
import { ProgramState } from '~/types/types';
import EnrollmentModule from '~/store/enrollment';
import { AxiosInstance, AxiosRequestConfig } from 'axios';
import { Settings } from '~/services/settings';

@InjectModule({ stateFactory: true }, module)
@OnClear<PatientModule>(async value => value.clear())
export default class PatientModule extends InjectVuexModule {

    @Resolve
    public settings!: Settings;

    @Resolve
    patientEnrollmentApi!: EdgePatientEnrollmentApi;
    @Resolve
    apiGateway!: ApigatewayApi;
    @Resolve
    edgeClaimApi!: EdgeClaimApi;
    @Resolve
    edgeCouponApi!: EdgeCouponApi;

    @Resolve
    axiosRequestConfig! : AxiosRequestConfig;

    @Resolve
    AxiosInstance! : AxiosInstance;

    @Resolve
    edgeSiteAccountApi!: EdgeSiteAccountApi;

    @Resolve
    edgeApi!: EdgeCardApi;

    @Resolve
    enrollmentModule !: EnrollmentModule;

    @Resolve
    edgeEnrollmentApi !: EdgeEnrollmentApi;

    currentPatient!: any;
    currentEmail = "";
    apiResult = '';

    patientInformation = {
        firstName: '',
        lastName: '',
        dateOfBirth: null as Moment | string | null,
        gender: null as Gender | null,
        addresses: [
            {
                addressOne: '',
                addressTwo: '',
                zip: '',
                city: '',
                state: '',
            },
        ],
        phoneNumbers: [
            {
                phoneNumber: '',
            },
        ],
        emailAddresses: [
            {
                address: '',
            },
        ],
    };

    @Mutation
    clear() {
        clearRecords(this.patientInformation);
    }

    @Mutation
    updatePatientInformation(data: typeof PatientModule.prototype.patientInformation) {
        Object.assign(this.patientInformation, data);
    }

    setCurrentPatient(data: any) {
        this.currentPatient = data;
    }
    @Mutation
    setCurrentEmailPatient(email:string){
        this.currentEmail = email;
    }
    getFromName(programState : number){
        switch(programState){
            case ProgramState.Gilead:
                return process.env.AA_EMAIL_FROM_NAME;
            default:
                return "";
        }
    }

    getFromEmailTemplate(programState : number){
        switch(programState){
            case ProgramState.Gilead:
                return process.env.AA_EMAIL_TEMPLATE;
            default:
                return "";
        }
    }

// conditional parameter for sendGrid email
    getParameter(programState : number){
        switch(programState){
            case ProgramState.Gilead:
                return  'firstname=' +
                encodeURIComponent(this.currentPatient.firstName!) +
                '&lastname=' +
                encodeURIComponent(this.currentPatient.lastName!) +
                '&MedMemberId=' +
                encodeURIComponent(this.currentPatient.memberNumber! || '') +
                '&PharMemberId=' +
                encodeURIComponent(this.currentPatient.pharmacyMemberNumber! || '') +
                '&UnsubscribeEmail='+
                encodeURIComponent(this.currentPatient.email)
            default:
                return "";
        }
    }

    async replaceCardByMail(programId: number) {
        try {
            const results = this.apiGateway.edgeCoupon.memberReplaceCard({
                memberId: this.currentPatient.memberNumber! || '',
                programId,
            });
            return results;
        } catch (error) {
            console.log(error);
            return;
        }
    }

    async replaceCardByMailQP(programId: number , memberNumber : string) {
        try {
            const results = this.edgeApi.medBenefitReplaceCard({
                memberNumber,
                programId,
            } , programId.toString());
            return results;
        } catch (error) {
            console.log(error);
            return;
        }
    }

    async replaceCardByEmail() {
        try {
            const programState = this.enrollmentModule.programState;
            let UpdatedEmail = this.currentEmail ? this.currentEmail : this.currentPatient.email;

            const emailRequest: SendRequestModel = {
                email: {
                    contact: {
                        emailAddress: UpdatedEmail,
                        firstName: this.currentPatient.firstName,
                        lastName: this.currentPatient.lastName,
                    },
                    fromAddress: process.env.EMAIL_FROM_ADDRESS,
                    fromName: this.getFromName(programState),
                    emailName:this.getFromEmailTemplate(programState),
                    deliveryType: 'triggered',
                    messageContentType: 1,
                    parameters:this.getParameter(programState),
                    memberNumber: this.currentPatient.memberNumber! || '',

                } as EmailSendEmailSingle,
                check: false,
            };
            return this.apiGateway.edgeEmail.emailSend(emailRequest);
        } catch (error) {
            console.log(error);
            return;
        }
    }

    async replaceCardByFax(programId: number , memberNumber : string) {
        try {
            const axios: AxiosInstance = (this.edgeApi as any).axios;
            const axiosRequest: AxiosRequestConfig = {
                url: 'edge/card/v2/Card/replaceCardAdvanced',
                method: 'POST',
                data: { 
                    replacementDeliveryTypes: 32, 
                    memberNumber: memberNumber
                },
                headers: {
                    'x-program-id': programId!,
                },
            };
            await Promise.all([
                (this.edgeApi as any).authentications.Token.applyToRequest(axiosRequest)
            ]);
            return await axios.request(axiosRequest);
        } catch (error) {
            console.log(error);
            return;
        }
    }

    async getEnrollmentDetailsQpProgram(memberId: string) {
        try {
            const results = await this.edgeClaimApi.claimAccumulatorSearch({
                memberNumbers: [memberId],
            });
            if (results && results.data && results.data.data) {
                return results.data.data;
            } else {
                return null;
            }
        } catch {
            return null;
        }
    }

    async updateDemographicsDetails(
        programId: number,
        demographicsModel: EnrollmentDemographicsModel,
        clientIdentifiers?: EnrollmentClientIdentifiersModel,
        insuranceModel?: EnrollmentInsuranceVerificationModel,
        pharmacyPayor?: BasicEnrollmentPharmacyPayor
    ) {

        try {

            let [mailOptInValue, emailOptInValue] = [false, false];
            if(demographicsModel['communicationTypes']?.valueOf() === 5) {
                emailOptInValue = true;
            } else if(demographicsModel['communicationTypes']?.valueOf() === 13) {
                mailOptInValue = true;
            } else if(demographicsModel.communicationTypes == 2) {
                emailOptInValue = true;
                mailOptInValue = true;
            }
            const newRequest = {
                      patient: {
                        firstName : demographicsModel.firstName,
                        lastName: demographicsModel.lastName,
                        dateOfBirth: moment.utc(demographicsModel.dateOfBirth || '', ['MM/DD/YYYY', 'YYYY-MM-DD']),
                        gender: "unknown", // this.currentPatient.gender
                        address: {
                            addressOne: demographicsModel.address1,
                            addressTwo: demographicsModel.address2,
                            city: demographicsModel.city,
                            state: demographicsModel.state,
                            zip: demographicsModel.postalCode
                        },
                        emailAddress: demographicsModel.email,
                        homePhone: demographicsModel.homePhone,
                        phoneNumbers: [{
                            phoneNumber: demographicsModel.homePhone,
                        }],
                        thirdPartyId: clientIdentifiers?.alternateId,
                        emailOptIn: emailOptInValue,
                        mailOptIn: mailOptInValue,
                    },
                    medicalPayors: [{
                        policyName: insuranceModel?.payerName,
                        policyNumber: insuranceModel?.payerMedicalId,
                    }],
                    pharmacyPayors: [{
                        policyName: pharmacyPayor?.name,
                        policyNumber: pharmacyPayor?.policyNumber
                    }]
                } as BasicEnrollmentRequest;
                const accountId = this.currentPatient.accountId;
                const results = await this.edgeEnrollmentApi.clientEnrollmentUpdateEnrollment(accountId, newRequest, programId.toString());
                this.getUpdatedPatient(programId, newRequest);
                this.seteditErrorMsg(results.data.messages[0]);
                return !!results.data.accountId;

        } catch (error) {
            console.log(error);
            return null;
        }
    }

    @Mutation
    seteditErrorMsg(data: string) {
       this.apiResult = data;
    }

    public editErrorMsg(){
        return this.apiResult;
    }

    async getRedemptionListQp(requestBody: ClaimV1ClaimGroupViewSearchGroupViewSearchRequest) {
        try {
            const results = await this.edgeClaimApi.claimGroupViewSearch(requestBody);
            return results.data.data as ClaimClaimGroupViewExtendedModel[];
        } catch {
            return;
        }
    }


    async getUpdatedPatient(programId: number, request: any) {
        try {
            let patient, getPatientResponse;
            const patientData = request.patient;
            getPatientResponse = await this.edgeSiteAccountApi.patientsSearchAllProgramPatients({
                "maxRecordCount": 5000,
                "firstName": patientData.firstName,
                "lastName": patientData.lastName,
                    "dateOfBirth": moment(patientData.dateOfBirth),
                "city": patientData.city,
                "state": patientData.state,
                "zip": patientData.zip,
                // "gender": patientData.gender
            }, programId.toString());

            patient =
            getPatientResponse &&
            getPatientResponse.data &&
            getPatientResponse.data[0] &&
            getPatientResponse.data[0].patient;

            if (patient) {
                const convertedPatient: any = patient;
                convertedPatient.dateOfBirth =
                    convertedPatient.dateOfBirth && moment.isMoment(convertedPatient.dateOfBirth)
                        ? moment(convertedPatient.dateOfBirth).format('MM/DD/YYYY')
                        : '';
                if (_.isString(convertedPatient.address1)) {
                    const index = convertedPatient.address1.lastIndexOf(', ');
                    if (index >= 0) {
                        convertedPatient.address2 = convertedPatient.address1.slice(index + 2, convertedPatient.address1.length);
                        convertedPatient.address1 = convertedPatient.address1.slice(0, index);
                    }
                }
                if (convertedPatient.alternateCardIds && convertedPatient.alternateCardIds.length > 0) {
                    const altId = convertedPatient.alternateCardIds[0];
                    convertedPatient.alternateId =
                        altId.alternateCardNumber && altId.cardAlternateIdType === AlternateCardType.ThirdPartyID
                            ? altId.alternateCardNumber
                            : '';
                }
                this.setCurrentPatient(convertedPatient);
            }
            return;
        } catch (error) {
            console.log(error);
            return;
        }
    }

    async getProgramState(programId: number) {
        return ProgramState.Gilead;
    }
}
